import type { ReactNode } from 'react';
import { clsx } from 'clsx';

export interface KpiTileProps {
  label: string;
  value: string;
  sub?: string;
  icon?: ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'danger';
  loading?: boolean;
}

const TONES: Record<NonNullable<KpiTileProps['tone']>, string> = {
  default: 'border-slate-200 bg-white',
  success: 'border-emerald-200 bg-emerald-50/40',
  warning: 'border-amber-200 bg-amber-50/40',
  danger: 'border-rose-200 bg-rose-50/40',
};

export function KpiTile({
  label,
  value,
  sub,
  icon,
  tone = 'default',
  loading,
}: KpiTileProps) {
  return (
    <div
      className={clsx(
        'rounded-xl border px-4 py-3 flex flex-col gap-1',
        TONES[tone],
      )}
      aria-busy={loading || undefined}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-slate-400">
        {icon}
        <span>{label}</span>
      </div>
      {loading ? (
        <div className="h-7 w-3/4 rounded bg-slate-200 animate-pulse" />
      ) : (
        <div className="text-2xl font-bold text-navy-900 leading-tight">
          {value}
        </div>
      )}
      {sub ? <div className="text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

export function KpiSkeleton() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex flex-col gap-2">
      <div className="h-3 w-1/2 rounded bg-slate-200 animate-pulse" />
      <div className="h-7 w-3/4 rounded bg-slate-200 animate-pulse" />
    </div>
  );
}
